/* qeslint-disable */

import { Tweens } from "./util_tween";

export const STORAGE_PREFIX = "timeliner-";

/** *********************** */
// Utils
/** *********************** */

export function firstDefined(...args: any[]): any {
    for (let i = 0; i < args.length; i++) {
        if (typeof args[i] !== "undefined") {
            return args[i];
        }
    }

    return undefined;
}

export function style(element: HTMLElement, ...styles: Record<string, string>[]): void {
    for (let i = 0; i < styles.length; ++i) {
        const styleObj = styles[i];
        const keys = Object.keys(styleObj);

        for (let j = 0; j < keys.length; j++) {
            const s = keys[j];

            (element.style as any)[s] = styleObj[s];
        }
    }
}

export function formatFriendlySeconds(s: number, type?: string): string {
    // TODO Refactor to 60fps???
    // 20 mins * 60 sec = 1080
    // 1080s * 60fps = 1080 * 60 < Number.MAX_SAFE_INTEGER

    const rawSecs = Math.floor(s);
    const secs = rawSecs % 60;
    const rawMins = Math.floor(rawSecs / 60);
    const mins = rawMins % 60;

    const secsStr = (secs / 100).toFixed(2).substring(2);

    let str = `${mins}:${secsStr}`;

    if (s % 1 > 0) {
        const t2 = (s % 1) * 60;

        if (type === "frames") {
            str = `${secs}+${t2.toFixed(0)}f`;
        } else {
            str += (s % 1).toFixed(2).substring(1);
        }
        // else str = mins + ':' + secs_micro;
        // else str = secs_micro + 's'; /// .toFixed(2)
    }

    return str;
}

interface LayerValue {
    time: number;
    value: number;
    tween?: string | boolean;
}

interface Layer {
    values: LayerValue[];
    _mute?: boolean;
}

// get object at time
export function findTimeinLayer(layer: Layer, time: number): { index: number; object: LayerValue } | number {
    const values = layer.values;
    let i: number;
    let il: number;

    // TODO optimize by checking time / binary search

    for (i = 0, il = values.length; i < il; i++) {
        const value = values[i];

        if (value.time === time) {
            return {
                index: i,
                object: value
            };
        }

        if (value.time > time) {
            return i;
        }
    }

    return i;
}

interface TimeAtLayerResult {
    value: number;
    can_tween: boolean;
    keyframe: boolean;
    entry?: LayerValue;
    tween?: string | boolean;
}

/**
 * @param layer
 * @param time
 * @returns TimeAtLayerResult
 */
export function timeAtLayer(layer: Layer, time: number): TimeAtLayerResult | undefined {
    // Find the value of layer at t seconds.
    // this expect layer to be sorted
    // not the most optimized for now, but would do.

    const values = layer.values;
    let i: number;
    const il = values.length;
    let entry: LayerValue;
    let prevEntry: LayerValue;

    // can't do anything
    if (il === 0) {
        return undefined;
    }

    if (layer._mute) {
        return undefined;
    }

    // find boundary cases
    entry = values[0];

    if (time < entry.time) {
        return {
            value: entry.value,
            can_tween: false, // cannot tween
            keyframe: false // not on keyframe
        };
    }

    for (i = 0; i < il; i++) {
        prevEntry = entry;
        entry = values[i];

        if (time === entry.time) {
            // only exception is on the last KF, where we display tween from prev entry
            if (i === il - 1) {
                return {
                    // index: i,
                    entry: prevEntry,
                    tween: prevEntry.tween,
                    can_tween: il > 1,
                    value: entry.value,
                    keyframe: true
                };
            }

            return {
                // index: i,
                entry: entry,
                tween: entry.tween,
                can_tween: il > 1,
                value: entry.value,
                keyframe: true // il > 1
            };
        }

        if (time < entry.time) {
            // possibly a tween
            if (!prevEntry.tween) {
                // or if value is none
                return {
                    value: prevEntry.value,
                    tween: false,
                    entry: prevEntry,
                    can_tween: true,
                    keyframe: false
                };
            }

            // calculate tween
            const timeDiff = entry.time - prevEntry.time;
            const valueDiff = entry.value - prevEntry.value;
            const tween = prevEntry.tween as string;

            const dt = time - prevEntry.time;
            const amount = dt / timeDiff;
            const newValue = prevEntry.value + Tweens[tween](amount) * valueDiff;

            return {
                entry: prevEntry,
                value: newValue,
                tween: prevEntry.tween,
                can_tween: true,
                keyframe: false
            };
        }
    }

    // time is after all entries
    return {
        value: entry.value,
        can_tween: false,
        keyframe: false
    };
}

interface ProxyContext {
    [key: string]: any;
    run: (args: (wrapper: ProxyContext) => void) => ProxyContext;
}

export function proxyCtx(ctx: CanvasRenderingContext2D): ProxyContext {
    // Creates a proxy 2d context wrapper which
    // allows the fluent / chaining API.
    const wrapper: ProxyContext = {} as ProxyContext;

    function proxyFunction(c: string) {
        return function (this: ProxyContext, ...args: any[]) {
            // Warning: this doesn't return value of function call
            (ctx as any)[c].apply(ctx, args);

            return wrapper;
        };
    }

    function proxyProperty(c: string) {
        return function (this: ProxyContext, v: any) {
            (ctx as any)[c] = v;

            return wrapper;
        };
    }

    wrapper.run = function (args: (wrapper: ProxyContext) => void) {
        args(wrapper);

        return wrapper;
    };

    // Build proxy methods/properties for all enumerable own keys
    const ctxKeys = Object.keys(ctx);

    for (let i = 0; i < ctxKeys.length; i++) {
        const c = ctxKeys[i];
        const type = typeof (ctx as any)[c];

        switch (type) {
            case "object":
                break;
            case "function":
                wrapper[c] = proxyFunction(c);
                break;
            default:
                wrapper[c] = proxyProperty(c);
                break;
        }
    }

    // Some important CanvasRenderingContext2D methods (like save/restore)
    // may not appear in Object.keys(ctx) depending on the browser/runtime.
    // Ensure that the wrapper always exposes these chaining helpers.
    const essentialMethods = [
        "save",
        "restore",
        "scale",
        "translate",
        "rotate",
        "beginPath",
        "closePath",
        "rect",
        "clip",
        "moveTo",
        "lineTo",
        "stroke",
        "fill",
        "clearRect",
        "fillRect",
        "strokeRect"
    ];

    for (let i = 0; i < essentialMethods.length; i++) {
        const method = essentialMethods[i];

        if (!(wrapper as any)[method] && typeof (ctx as any)[method] === "function") {
            (wrapper as any)[method] = proxyFunction(method);
        }
    }

    // Some important style properties (like fillStyle) may also be non-enumerable.
    // Ensure we always expose proxies for them so calls like ctxWrap.fillStyle(...)
    // work reliably across browsers.
    const essentialProperties = [
        "fillStyle",
        "strokeStyle",
        "lineWidth",
        "font",
        "textAlign",
        "textBaseline",
        "globalAlpha"
    ];

    for (let i = 0; i < essentialProperties.length; i++) {
        const prop = essentialProperties[i];

        if (!(wrapper as any)[prop] && prop in ctx) {
            (wrapper as any)[prop] = proxyProperty(prop);
        }
    }

    return wrapper;
}

export const utils = {
    STORAGE_PREFIX: STORAGE_PREFIX,
    firstDefined: firstDefined,
    style: style,
    format_friendly_seconds: formatFriendlySeconds,
    findTimeinLayer: findTimeinLayer,
    timeAtLayer: timeAtLayer,
    proxy_ctx: proxyCtx
};
